// A free workspace's own SMS account (BulkGate), and whose SMS go out for a workspace.
import { z } from "zod";
import { IsoDateTime } from "./common";

/** Like a key's: `unverified` when BulkGate could not be reached at saving, so nothing is known yet and the account is still tried. */
export const SMS_PROVIDER_STATUSES = ["ok", "invalid", "no_credit", "unverified"] as const;
export type SmsProviderStatus = (typeof SMS_PROVIDER_STATUSES)[number];

/** Why the SMS go out where they go. */
export const SMS_ROUTE_REASONS = ["host", "paid", "grant", "free", "trial", "unpaid"] as const;
export type SmsRouteReason = (typeof SMS_ROUTE_REASONS)[number];

/**
 * What stops SMS to customers. Each is also the reason written on the refused message and the error code of a refused send.
 * `billing_required`: an unpaid Stripe workspace with no working BulkGate of its own, which never sends on the service's account.
 */
export const SMS_BLOCKS = ["sms_provider_required", "sms_provider_invalid", "sms_provider_no_credit", "sms_trial_off", "sms_cap_reached", "billing_required"] as const;
export type SmsBlock = (typeof SMS_BLOCKS)[number];

export const SmsProviderDto = z.object({
  provider: z.literal("bulkgate"),
  appId: z.string(),
  /** The only part of the token that ever leaves the service. */
  tokenLast4: z.string(),
  senderId: z.string().nullable(),
  status: z.enum(SMS_PROVIDER_STATUSES),
  checkedAt: IsoDateTime.nullable(),
  /** A stable code the UI translates, such as `http_401` or `low_credit`. */
  lastErrorCode: z.string().nullable(),
  /** BulkGate's answer at the last check, in its own credits. Null when it was not read. */
  credit: z.number().nullable(),
  createdAt: IsoDateTime,
});
export type SmsProviderDto = z.infer<typeof SmsProviderDto>;

export const SmsProviderSaveRequest = z
  .object({
    appId: z.string().trim().regex(/^\d{1,12}$/),
    token: z.string().trim().min(8).max(200),
    /** Registered at BulkGate as a text sender: letters and digits, at most 11. */
    senderId: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9 ]{1,11}$/)
      .nullable()
      .optional(),
  })
  .strict();
export type SmsProviderSaveRequest = z.infer<typeof SmsProviderSaveRequest>;

export const SmsStatusDto = z.object({
  source: z.enum(["service", "tenant_bulkgate", "none"]),
  reason: z.enum(SMS_ROUTE_REASONS),
  /** Set when SMS to customers are refused, with the reason the app shows. */
  blocked: z.enum(SMS_BLOCKS).nullable(),
  /** Only a free workspace, or an unpaid one past its grace, sends on an account of its own. */
  ownAccount: z.boolean(),
  provider: SmsProviderDto.nullable(),
  /** The address to paste into the BulkGate application, for delivery reports and STOP replies. The owner's eyes only. */
  webhookUrl: z.string().nullable(),
  /** The operator's "AI and SMS on us", when it applies. */
  grant: z.object({ monthlyCapUsd: z.number() }).nullable(),
});
export type SmsStatusDto = z.infer<typeof SmsStatusDto>;

/** The guided start's key step: whether a free or trial workspace still needs a key, and when the step was shown and done. */
export const AiKeyStepDto = z.object({ needed: z.boolean(), shownAt: z.string().nullable(), doneAt: z.string().nullable() });
export type AiKeyStepDto = z.infer<typeof AiKeyStepDto>;
